import { Container, Typography, Box, List, ListItem, ListItemIcon, ListItemText, Paper, Chip } from '@mui/material';
import { CheckCircle, Lock, Help, Settings, Description, AttachMoney, Receipt, AccountCircle, Email, Phone } from '@mui/icons-material';
import { styled } from '@mui/system';

const HelpContainer = styled(Container)({
  maxWidth: '1200px',
  padding: '2rem',
  '@media (max-width: 600px)': {
    padding: '1rem',
  },
  '@media (min-width: 601px) and (max-width: 900px)': {
    padding: '1.5rem',
  },
  '@media (min-width: 901px)': {
    padding: '2rem',
  },
});

const Section = styled(Paper)(({ }) => ({
  marginBottom: '2rem',
  padding: '2rem',
  borderRadius: '10px',
  boxShadow: '0px 3px 3px -2px rgba(0,0,0,0.2), 0px 3px 4px 0px rgba(0,0,0,0.14), 0px 1px 8px 0px rgba(0,0,0,0.12)',
  '@media (max-width: 600px)': {
    marginBottom: '1rem',
    padding: '1rem',
  },
  '@media (min-width: 601px) and (max-width: 900px)': {
    marginBottom: '1.5rem',
    padding: '1.5rem',
  },
  '@media (min-width: 901px)': {
    marginBottom: '2rem',
    padding: '2rem',
  },
}));

const HelpGuide = () => {

  return (
    <HelpContainer>
      <Typography variant="h4" gutterBottom sx={{ 
        fontWeight: 'bold', 
        mb: 4, 
        color: '#2c3e50',
        '@media (max-width: 600px)': {
          fontSize: '1.5rem',
          mb: 2
        },
        '@media (min-width: 601px) and (max-width: 900px)': {
          fontSize: '1.75rem',
          mb: 3
        },
        '@media (min-width: 901px)': {
          fontSize: '2rem',
          mb: 4
        }
      }}>
        Welcome to the BooksComply Help Portal!
      </Typography>
      <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
        This guide walks you through the key features and functionalities available to help you navigate and make the most of our platform.
      </Typography>

      <Section>
        <Box display="flex" alignItems="center" mb={2}>
          <Help color="primary" sx={{ 
            mr: 1, 
            fontSize: '2rem',
            '@media (max-width: 600px)': {
              fontSize: '1.5rem'
            },
            '@media (min-width: 601px) and (max-width: 900px)': {
              fontSize: '1.75rem'
            },
            '@media (min-width: 901px)': {
              fontSize: '2rem'
            }
          }} />
          <Typography variant="h5" sx={{ 
            fontWeight: 'bold',
            '@media (max-width: 600px)': {
              fontSize: '1.25rem'
            },
            '@media (min-width: 601px) and (max-width: 900px)': {
              fontSize: '1.4rem'
            },
            '@media (min-width: 901px)': {
              fontSize: '1.5rem'
            }
          }}>Help Guide</Typography>
        </Box>
        <Typography variant="body1">
          Explore how the portal works in detail through our Help Guide. Find answers, instructions, and tips to use every section efficiently.
        </Typography>
      </Section>

      <Section>
        <Box display="flex" alignItems="center" mb={2}>
          <Settings color="primary" sx={{ 
            mr: 1, 
            fontSize: '2rem',
            '@media (max-width: 600px)': {
              fontSize: '1.5rem'
            },
            '@media (min-width: 601px) and (max-width: 900px)': {
              fontSize: '1.75rem'
            },
            '@media (min-width: 901px)': {
              fontSize: '2rem'
            }
          }} />
          <Typography variant="h5" sx={{ 
            fontWeight: 'bold',
            '@media (max-width: 600px)': {
              fontSize: '1.25rem'
            },
            '@media (min-width: 601px) and (max-width: 900px)': {
              fontSize: '1.4rem'
            },
            '@media (min-width: 901px)': {
              fontSize: '1.5rem'
            }
          }}>Account Settings</Typography>
        </Box>
        <Typography variant="body1" mb={2}>
          The Account Settings section is divided into three key parts:
        </Typography>
        <List>
          <ListItem>
            <ListItemIcon>
              <AccountCircle color="primary" />
            </ListItemIcon>
            <ListItemText primary="My Profile" secondary="View and update your personal details" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <Receipt color="primary" />
            </ListItemIcon>
            <ListItemText primary="Invoice Download" secondary="Access invoices for all online payments made through the portal" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <AttachMoney color="primary" />
            </ListItemIcon>
            <ListItemText primary="Payment History" secondary="View the complete history of your transactions through website portal" />
          </ListItem>
        </List>
      </Section>

      <Section>
        <Box display="flex" alignItems="center" mb={2}>
          <Description color="primary" sx={{ 
            mr: 1, 
            fontSize: '2rem',
            '@media (max-width: 600px)': {
              fontSize: '1.5rem'
            },
            '@media (min-width: 601px) and (max-width: 900px)': {
              fontSize: '1.75rem'
            },
            '@media (min-width: 901px)': {
              fontSize: '2rem'
            }
          }} />
          <Typography variant="h5" sx={{ 
            fontWeight: 'bold',
            '@media (max-width: 600px)': {
              fontSize: '1.25rem'
            },
            '@media (min-width: 601px) and (max-width: 900px)': {
              fontSize: '1.4rem'
            },
            '@media (min-width: 901px)': {
              fontSize: '1.5rem'
            }
          }}>Documents</Typography>
        </Box>
        <Typography variant="body1" mb={2}>
          Your documents are organized into two categories:
        </Typography>
        <Typography variant="h6" sx={{ 
          fontWeight: 'bold', 
          mt: 2,
          '@media (max-width: 600px)': {
            fontSize: '1rem'
          },
          '@media (min-width: 601px) and (max-width: 900px)': {
            fontSize: '1.1rem'
          },
          '@media (min-width: 901px)': {
            fontSize: '1.25rem'
          }
        }}>1. Main Documents</Typography>
        <Typography variant="body1" sx={{ 
          ml: 2, 
          mb: 2,
          '@media (max-width: 600px)': {
            ml: 1
          },
          '@media (min-width: 601px) and (max-width: 900px)': {
            ml: 1.5
          },
          '@media (min-width: 901px)': {
            ml: 2
          }
        }}>
          • Contains files uploaded during Step 1: Initial Information Required.
          <br />
          • Easily accessible for your review at any time.
        </Typography>
        <Typography variant="h6" sx={{ 
          fontWeight: 'bold', 
          mt: 2,
          '@media (max-width: 600px)': {
            fontSize: '1rem'
          },
          '@media (min-width: 601px) and (max-width: 900px)': {
            fontSize: '1.1rem'
          },
          '@media (min-width: 901px)': {
            fontSize: '1.25rem'
          }
        }}>2. Chat Documents</Typography>
        <Typography variant="body1" sx={{ 
          ml: 2,
          '@media (max-width: 600px)': {
            ml: 1
          },
          '@media (min-width: 601px) and (max-width: 900px)': {
            ml: 1.5
          },
          '@media (min-width: 901px)': {
            ml: 2
          }
        }}>
          • Includes files shared during your conversations with our team through the chat feature.
          <br />
          • All files exchanged in chats are stored here.
        </Typography>
      </Section>

      <Section>
        <Typography variant="h5" sx={{ 
          fontWeight: 'bold', 
          mb: 2,
          '@media (max-width: 600px)': {
            fontSize: '1.25rem',
            mb: 1
          },
          '@media (min-width: 601px) and (max-width: 900px)': {
            fontSize: '1.4rem',
            mb: 1.5
          },
          '@media (min-width: 901px)': {
            fontSize: '1.5rem',
            mb: 2
          }
        }}>Pricing Plan: Services You Can Use</Typography>
        <List>
          <ListItem>
            <ListItemIcon>
              <CheckCircle color="success" />
            </ListItemIcon>
            <ListItemText primary="Tax Filing (Active)" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <Lock color="action" />
            </ListItemIcon>
            <ListItemText primary="Bookkeeping (Upgrade available)" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <Lock color="action" />
            </ListItemIcon>
            <ListItemText primary="USA Company Setup (Optional)" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <Lock color="action" />
            </ListItemIcon>
            <ListItemText primary="CFO Advisory (On demand)" />
          </ListItem>
        </List>
      </Section>

      <Section>
        <Typography variant="h5" sx={{ 
          fontWeight: 'bold', 
          mb: 3,
          '@media (max-width: 600px)': {
            fontSize: '1.25rem',
            mb: 1.5
          },
          '@media (min-width: 601px) and (max-width: 900px)': {
            fontSize: '1.4rem',
            mb: 2
          },
          '@media (min-width: 901px)': {
            fontSize: '1.5rem',
            mb: 3
          }
        }}>Tax Filing Portal: Track Your Tax Return</Typography>
        <Typography variant="body1" mb={3}>
          Stay updated with your return's progress through these steps:
        </Typography>
        
        <Box sx={{ 
          display: 'grid', 
          gap: 3, 
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(4, 1fr)' 
          },
          '@media (max-width: 600px)': {
            gap: 2
          },
          '@media (min-width: 601px) and (max-width: 900px)': {
            gap: 2.5
          },
          '@media (min-width: 901px)': {
            gap: 3
          }
        }}>
          {/* Step 1 Card */}
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              borderRadius: 2, 
              backgroundColor: '#2b2b29',
              color: 'white',
              minHeight: '180px',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.3s ease',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
              '@media (max-width: 600px)': {
                p: 2,
                minHeight: '140px'
              },
              '@media (min-width: 601px) and (max-width: 900px)': {
                p: 2.5,
                minHeight: '160px'
              },
              '@media (min-width: 901px)': {
                p: 3,
                minHeight: '180px'
              }
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
              <Typography variant="h6" sx={{ 
                fontWeight: '600', 
                fontSize: '1.1rem',
                '@media (max-width: 600px)': {
                  fontSize: '0.9rem'
                },
                '@media (min-width: 601px) and (max-width: 900px)': {
                  fontSize: '1rem'
                },
                '@media (min-width: 901px)': {
                  fontSize: '1.1rem'
                }
              }}>Step 1</Typography>
              <Chip 
                label="IN PROGRESS" 
                size="small" 
                sx={{ 
                  backgroundColor: '#2d3748',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '0.65rem',
                  height: '22px',
                  borderRadius: '4px',
                  textTransform: 'uppercase',
                  '@media (max-width: 600px)': {
                    fontSize: '0.6rem',
                    height: '18px'
                  },
                  '@media (min-width: 601px) and (max-width: 900px)': {
                    fontSize: '0.65rem',
                    height: '20px'
                  },
                  '@media (min-width: 901px)': {
                    fontSize: '0.65rem',
                    height: '22px'
                  }
                }} 
              />
            </Box>
            <Typography variant="h6" sx={{ 
              fontWeight: '500', 
              mb: 2, 
              flexGrow: 1,
              '@media (max-width: 600px)': {
                fontSize: '0.9rem'
              },
              '@media (min-width: 601px) and (max-width: 900px)': {
                fontSize: '1rem'
              },
              '@media (min-width: 901px)': {
                fontSize: '1.1rem'
              }
            }}>Initial Information Required</Typography>
            <Box>
              <Box sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                height: '6px',
                borderRadius: '3px',
                overflow: 'hidden'
              }}>
                <Box 
                  sx={{
                    backgroundColor: 'white',
                    height: '100%',
                    width: '0%',
                    transition: 'width 0.3s ease'
                  }}
                />
              </Box>
            </Box>
          </Paper>

          {/* Step 2 Card */}
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              borderRadius: 2, 
              backgroundColor: '#1d4b81',
              color: 'white',
              minHeight: '180px',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.3s ease',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
              '@media (max-width: 600px)': {
                p: 2,
                minHeight: '140px'
              },
              '@media (min-width: 601px) and (max-width: 900px)': {
                p: 2.5,
                minHeight: '160px'
              },
              '@media (min-width: 901px)': {
                p: 3,
                minHeight: '180px'
              }
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
              <Typography variant="h6" sx={{ 
                fontWeight: '600', 
                fontSize: '1.1rem',
                '@media (max-width: 600px)': {
                  fontSize: '0.9rem'
                },
                '@media (min-width: 601px) and (max-width: 900px)': {
                  fontSize: '1rem'
                },
                '@media (min-width: 901px)': {
                  fontSize: '1.1rem'
                }
              }}>Step 2</Typography>
              <Chip 
                label="PENDING" 
                size="small" 
                sx={{ 
                  backgroundColor: '#2b6cb0',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '0.65rem',
                  height: '22px',
                  borderRadius: '4px',
                  textTransform: 'uppercase',
                  '@media (max-width: 600px)': {
                    fontSize: '0.6rem',
                    height: '18px'
                  },
                  '@media (min-width: 601px) and (max-width: 900px)': {
                    fontSize: '0.65rem',
                    height: '20px'
                  },
                  '@media (min-width: 901px)': {
                    fontSize: '0.65rem',
                    height: '22px'
                  }
                }} 
              />
            </Box>
            <Typography variant="h6" sx={{ 
              fontWeight: '500', 
              mb: 2, 
              flexGrow: 1,
              '@media (max-width: 600px)': {
                fontSize: '0.9rem'
              },
              '@media (min-width: 601px) and (max-width: 900px)': {
                fontSize: '1rem'
              },
              '@media (min-width: 901px)': {
                fontSize: '1.1rem'
              }
            }}>Tax Return Preparation</Typography>
          </Paper>

          {/* Step 3 Card */}
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              borderRadius: 2, 
              backgroundColor: '#1d4b81',
              color: 'white',
              minHeight: '180px',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.3s ease',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
              '@media (max-width: 600px)': {
                p: 2,
                minHeight: '140px'
              },
              '@media (min-width: 601px) and (max-width: 900px)': {
                p: 2.5,
                minHeight: '160px'
              },
              '@media (min-width: 901px)': {
                p: 3,
                minHeight: '180px'
              }
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
              <Typography variant="h6" sx={{ 
                fontWeight: '600', 
                fontSize: '1.1rem',
                '@media (max-width: 600px)': {
                  fontSize: '0.9rem'
                },
                '@media (min-width: 601px) and (max-width: 900px)': {
                  fontSize: '1rem'
                },
                '@media (min-width: 901px)': {
                  fontSize: '1.1rem'
                }
              }}>Step 3</Typography>
              <Chip 
                label="PENDING" 
                size="small" 
                sx={{ 
                  backgroundColor: '#2b6cb0',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '0.65rem',
                  height: '22px',
                  borderRadius: '4px',
                  textTransform: 'uppercase',
                  '@media (max-width: 600px)': {
                    fontSize: '0.6rem',
                    height: '18px'
                  },
                  '@media (min-width: 601px) and (max-width: 900px)': {
                    fontSize: '0.65rem',
                    height: '20px'
                  },
                  '@media (min-width: 901px)': {
                    fontSize: '0.65rem',
                    height: '22px'
                  }
                }} 
              />
            </Box>
            <Typography variant="h6" sx={{ 
              fontWeight: '500', 
              mb: 2, 
              flexGrow: 1,
              '@media (max-width: 600px)': {
                fontSize: '0.9rem'
              },
              '@media (min-width: 601px) and (max-width: 900px)': {
                fontSize: '1rem'
              },
              '@media (min-width: 901px)': {
                fontSize: '1.1rem'
              }
            }}>Draft Tax Return</Typography>
          </Paper>

          {/* Step 4 Card */}
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              borderRadius: 2, 
              backgroundColor: '#1d4b81',
              color: 'white',
              minHeight: '180px',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.3s ease',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
              '@media (max-width: 600px)': {
                p: 2,
                minHeight: '140px'
              },
              '@media (min-width: 601px) and (max-width: 900px)': {
                p: 2.5,
                minHeight: '160px'
              },
              '@media (min-width: 901px)': {
                p: 3,
                minHeight: '180px'
              }
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
              <Typography variant="h6" sx={{ 
                fontWeight: '600', 
                fontSize: '1.1rem',
                '@media (max-width: 600px)': {
                  fontSize: '0.9rem'
                },
                '@media (min-width: 601px) and (max-width: 900px)': {
                  fontSize: '1rem'
                },
                '@media (min-width: 901px)': {
                  fontSize: '1.1rem'
                }
              }}>Step 4</Typography>
              <Chip 
                label="PENDING" 
                size="small" 
                sx={{ 
                  backgroundColor: '#2b6cb0',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '0.65rem',
                  height: '22px',
                  borderRadius: '4px',
                  textTransform: 'uppercase',
                  '@media (max-width: 600px)': {
                    fontSize: '0.6rem',
                    height: '18px'
                  },
                  '@media (min-width: 601px) and (max-width: 900px)': {
                    fontSize: '0.65rem',
                    height: '20px'
                  },
                  '@media (min-width: 901px)': {
                    fontSize: '0.65rem',
                    height: '22px'
                  }
                }} 
              />
            </Box>
            <Typography variant="h6" sx={{ 
              fontWeight: '500', 
              mb: 2, 
              flexGrow: 1,
              '@media (max-width: 600px)': {
                fontSize: '0.9rem'
              },
              '@media (min-width: 601px) and (max-width: 900px)': {
                fontSize: '1rem'
              },
              '@media (min-width: 901px)': {
                fontSize: '1.1rem'
              }
            }}>Tax Filing</Typography>
          </Paper>
        </Box>

      </Section>

      <Section>
        <Typography variant="h5" sx={{ 
          fontWeight: 'bold', 
          mb: 2,
          '@media (max-width: 600px)': {
            fontSize: '1.25rem',
            mb: 1
          },
          '@media (min-width: 601px) and (max-width: 900px)': {
            fontSize: '1.4rem',
            mb: 1.5
          },
          '@media (min-width: 901px)': {
            fontSize: '1.5rem',
            mb: 2
          }
        }}>Support/Questions/Connect with us:</Typography>
        <Typography variant="body1" mb={2}>
          • Click the <strong>Chat with us!</strong> button (top-right) for support.
        </Typography>
        
        <Typography variant="h6" sx={{ 
          fontWeight: 'bold', 
          mt: 3, 
          mb: 1,
          '@media (max-width: 600px)': {
            fontSize: '1rem',
            mt: 2
          },
          '@media (min-width: 601px) and (max-width: 900px)': {
            fontSize: '1.1rem',
            mt: 2.5
          },
          '@media (min-width: 901px)': {
            fontSize: '1.25rem',
            mt: 3
          }
        }}>Need Help?</Typography>
        <List>
          <ListItem>
            <ListItemIcon>
              <Email color="primary" />
            </ListItemIcon>
            <ListItemText primary="Email:" secondary="contact@bookscomply.com" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <Phone color="primary" />
            </ListItemIcon>
            <ListItemText primary="WhatsApp:" secondary="+91-9428482371" />
          </ListItem>
        </List>
        
        <Typography variant="body1" mt={2}>
          Notifications (🔔) will alert you when a message/task needs your attention.
        </Typography>
      </Section>
    </HelpContainer>
  );
};

export default HelpGuide;